<template>
  <div :class="['metric-card', 'm-surface', { 'is-clickable': clickable }]" @click="$emit('click')">
    <div class="card-head">
      <span class="card-eyebrow">{{ label }}</span>
      <slot name="extra">
        <DeltaPill v-if="delta !== undefined" :value="delta" :suffix="deltaSuffix" />
      </slot>
    </div>

    <div class="card-figure">
      <span v-if="prefix" class="figure-prefix">{{ prefix }}</span>
      <Odometer :value="value" :decimals="decimals" :group="true" />
      <span v-if="unit" class="figure-unit">{{ unit }}</span>
    </div>

    <div v-if="hint || $slots.hint" class="card-hint">
      <slot name="hint">
        <span>{{ hint }}</span>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Odometer from './Odometer.vue'
import DeltaPill from './DeltaPill.vue'

export default defineComponent({
  name: 'MetricCard',
  components: {
    Odometer,
    DeltaPill
  },
  props: {
    label: {
      type: String,
      required: true
    },
    value: {
      type: [Number, String],
      required: true
    },
    decimals: {
      type: Number,
      default: 0
    },
    prefix: {
      type: String,
      default: ''
    },
    unit: {
      type: String,
      default: ''
    },
    delta: {
      type: Number,
      default: undefined
    },
    deltaSuffix: {
      type: String,
      default: '%'
    },
    hint: {
      type: String,
      default: ''
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click']
})
</script>

<style scoped lang="scss">
.metric-card {
  padding: 20px 22px;
  border-radius: var(--r-md);
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;

  &.is-clickable {
    cursor: pointer;
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--e2);
    }
  }
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-eyebrow {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-muted);
  letter-spacing: 0.02em;
}

.card-figure {
  font-size: var(--fs-display-m);
  font-weight: 700;
  line-height: 1;
  color: var(--ink);
  letter-spacing: -0.035em;
  display: flex;
  align-items: baseline;
  gap: 3px;
  font-family: var(--font-mono);
}

.figure-prefix {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink-soft);
  margin-right: 2px;
}

.figure-unit {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-soft);
  margin-left: 2px;
}

.card-hint {
  font-size: 12px;
  color: var(--ink-faint);
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
